import type { InvoiceBlockKind, InvoiceBlockResponse } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';

/**
 * Sprint-6 E.1 Issue 3: Live-Berechnung pro Posten.
 * Spiegelt 1:1 die Backend-Logik in InvoiceService.BuildLines.
 */
export interface BlockComputation {
  quantity: number;
  total: number;
  /** Kurzform-Label fuer die Row, z.B. "5,00 €/Person/Nacht". */
  shortRate: string;
  /** Lange Form, z.B. "= 30,00 € bei 3 Nächten und 2 Gästen". */
  computedLabel: string;
}

const SUFFIX: Record<InvoiceBlockKind, string> = {
  PerStay: 'pro Aufenthalt',
  PerNight: 'pro Nacht',
  PerPersonPerStay: 'pro Person und Aufenthalt',
  PerPersonPerNight: 'pro Person und Nacht',
};

const SHORT: Record<InvoiceBlockKind, string> = {
  PerStay: 'pauschal',
  PerNight: 'pro Nacht',
  PerPersonPerStay: 'pro Person',
  PerPersonPerNight: 'pro Person/Nacht',
};

export function quantityFor(kind: InvoiceBlockKind, nights: number, guests: number): number {
  switch (kind) {
    case 'PerStay':
      return 1;
    case 'PerNight':
      return nights;
    case 'PerPersonPerStay':
      return guests;
    case 'PerPersonPerNight':
      return nights * guests;
  }
}

/** „3 Nächten und 2 Gästen" — ausgeschrieben statt „3N × 2P". */
export function stayShorthand(nights: number, guests: number): string {
  const nightWord = nights === 1 ? 'Nacht' : 'Nächten';
  const guestWord = guests === 1 ? 'Gast' : 'Gästen';
  return `${nights} ${nightWord} und ${guests} ${guestWord}`;
}

export function computeBlock(
  block: Pick<InvoiceBlockResponse, 'kind' | 'unitPrice' | 'currency'>,
  nights: number,
  guests: number,
): BlockComputation {
  const qty = quantityFor(block.kind, nights, guests);
  const total = qty * block.unitPrice;
  return {
    quantity: qty,
    total,
    shortRate: `${formatMoney(block.unitPrice, block.currency)} ${SHORT[block.kind]}`,
    computedLabel: `= ${formatMoney(total, block.currency)} bei ${stayShorthand(nights, guests)}`,
  };
}

export function inlineRateText(
  block: Pick<InvoiceBlockResponse, 'kind' | 'unitPrice' | 'currency'>,
): string {
  return `${formatMoney(block.unitPrice, block.currency)} · ${SUFFIX[block.kind]}`;
}
